import * as THREE from 'three';
import { jitter, seededRandom } from '../../Style.js';

// Shooters that hurl fire rather than loose bolts.
const FIRE_SHOOTERS = new Set(['rocket_launcher', 'airplane', 'super_helicopter']);

// Geometries are shared by every projectile; only materials are per-team.
// Bolts are modelled along +Z so Object3D.lookAt points them down the flight path.
const GEOMETRY = {
  shaft: new THREE.CylinderGeometry(0.05, 0.05, 1.6, 5).rotateX(Math.PI / 2),
  tip: new THREE.ConeGeometry(0.12, 0.35, 5).rotateX(Math.PI / 2).translate(0, 0, 0.95),
  fletch: new THREE.PlaneGeometry(0.28, 0.45).rotateY(Math.PI / 2).translate(0, 0, -0.65),
  core: new THREE.IcosahedronGeometry(0.35, 0),
  glow: new THREE.IcosahedronGeometry(0.7, 1),
  // Chiselled rock hurled by trebuchets
  boulder: jitter(new THREE.IcosahedronGeometry(0.42, 0), 0.16, seededRandom(31)),
  // Flames streaming behind the boulder (the projectile faces its target along +Z)
  flameTail: new THREE.ConeGeometry(0.5, 1.6, 7).rotateX(-Math.PI / 2).translate(0, 0, -0.7),
  // Elongated halo around a bolt, so it reads at gameplay zoom
  streak: new THREE.SphereGeometry(1, 8, 6).scale(0.32, 0.32, 1.5)
};

// Bolts are drawn larger than life: at gameplay zoom a real-sized bolt is invisible
const BOLT_SCALE = 1.7;

const SHAFT_MATERIAL = new THREE.MeshLambertMaterial({ color: 0x8a5a34, flatShading: true });
const TIP_MATERIAL = new THREE.MeshLambertMaterial({ color: 0x4a4a4e, flatShading: true });
const CORE_MATERIAL = new THREE.MeshBasicMaterial({ color: 0xfff0b0 });
const BOULDER_MATERIAL = new THREE.MeshLambertMaterial({ color: 0x4a443e, emissive: 0x5a1e04, flatShading: true });
// Back faces only: the flames show as a halo around the boulder without washing over its face
const BOULDER_FLAME_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xff5a10,
  transparent: true,
  opacity: 0.6,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
  side: THREE.BackSide
});
const FLAME_TAIL_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xff6a14,
  transparent: true,
  opacity: 0.4,
  blending: THREE.AdditiveBlending,
  depthWrite: false
});
const GLOW_MATERIAL = new THREE.MeshBasicMaterial({
  color: 0xff7a1a,
  transparent: true,
  opacity: 0.55,
  blending: THREE.AdditiveBlending,
  depthWrite: false
});

const streakMaterials = new Map();
function streakMaterial(color) {
  if (!streakMaterials.has(color)) {
    streakMaterials.set(color, new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    }));
  }
  return streakMaterials.get(color);
}

const fletchMaterials = new Map();
function fletchMaterial(color) {
  if (!fletchMaterials.has(color)) {
    fletchMaterials.set(color, new THREE.MeshLambertMaterial({ color, side: THREE.DoubleSide }));
  }
  return fletchMaterials.get(color);
}

// Soft round sprite so embers glow instead of drawing as hard squares
const EMBER_TEXTURE = (() => {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 32;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.3, 'rgba(255,255,255,0.6)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 32, 32);
  return new THREE.CanvasTexture(canvas);
})();

const EMBER_HOT = new THREE.Color(0xffc040);
const EMBER_COOL = new THREE.Color(0xff3a00);

export class Projectile {
  // shooterType is the shooter's unit type ('player', 'tank', 'sniper', ...) or 'turret'.
  constructor(scene, projectileData, color, shooterType = null) {
    this.scene = scene;
    this.data = projectileData;
    this.shooterType = shooterType;
    this.isFire = FIRE_SHOOTERS.has(shooterType);
    this.mesh = null;
    this.trail = null;
    this.embers = null;
    this.trailPositions = [];
    this.maxTrailLength = this.isFire ? 14 : 6;
    this.create(color);
  }

  create(color) {
    this.mesh = new THREE.Group();
    if (this.isFire) {
      this.createFireball();
    } else {
      this.createBolt(color);
    }

    this.updatePosition(this.data.position);
    this.orient();

    this.createTrail(color);

    this.scene.add(this.mesh);
  }

  createBolt(color) {
    const fletch = fletchMaterial(color);
    this.mesh.add(new THREE.Mesh(GEOMETRY.shaft, SHAFT_MATERIAL));
    this.mesh.add(new THREE.Mesh(GEOMETRY.tip, TIP_MATERIAL));

    // Two crossed vanes in the team colour
    const vaneA = new THREE.Mesh(GEOMETRY.fletch, fletch);
    const vaneB = new THREE.Mesh(GEOMETRY.fletch, fletch);
    vaneB.rotation.z = Math.PI / 2;
    this.mesh.add(vaneA, vaneB);

    // Glowing team-coloured halo makes incoming fire easy to spot
    this.mesh.add(new THREE.Mesh(GEOMETRY.streak, streakMaterial(color)));
    this.mesh.scale.setScalar(BOLT_SCALE);
  }

  createFireball() {
    // Dragons spit bigger fireballs than mages and wyverns
    const scale = this.shooterType === 'super_helicopter' ? 1.6 : this.shooterType === 'airplane' ? 0.8 : 1;
    this.mesh.scale.setScalar(scale);

    if (this.shooterType === 'rocket_launcher') {
      // Trebuchets hurl a flaming boulder that tumbles in flight
      this.core = new THREE.Mesh(GEOMETRY.boulder, BOULDER_MATERIAL);
      this.core.rotation.set(Math.random() * 6, Math.random() * 6, 0);
      this.glow = new THREE.Mesh(GEOMETRY.glow, BOULDER_FLAME_MATERIAL);
      this.mesh.add(new THREE.Mesh(GEOMETRY.flameTail, FLAME_TAIL_MATERIAL));
      this.tumble = true;
    } else {
      this.core = new THREE.Mesh(GEOMETRY.core, CORE_MATERIAL);
      this.glow = new THREE.Mesh(GEOMETRY.glow, GLOW_MATERIAL);
    }
    this.mesh.add(this.core, this.glow);
    this.flickerSeed = Math.random() * 100;
  }

  // Point the bolt along its flight path (start -> end)
  orient() {
    const end = this.data.endPos;
    if (!end) return;
    const target = new THREE.Vector3(end.x, end.y, end.z);
    if (target.distanceToSquared(this.mesh.position) > 0.0001) {
      this.mesh.lookAt(target);
    }
  }

  createTrail(color) {
    const trailGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(this.maxTrailLength * 3);
    trailGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    if (this.isFire) {
      // Ember trail: additive points cooling from gold to red as they age
      const colors = new Float32Array(this.maxTrailLength * 3);
      trailGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      const emberMaterial = new THREE.PointsMaterial({
        size: 0.9 * this.mesh.scale.x,
        map: EMBER_TEXTURE,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false
      });
      this.embers = new THREE.Points(trailGeometry, emberMaterial);
      this.scene.add(this.embers);

      // A faint team-coloured streak keeps ownership readable
      const streakGeometry = new THREE.BufferGeometry();
      streakGeometry.setAttribute('position', trailGeometry.getAttribute('position'));
      this.trail = new THREE.Line(streakGeometry, new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.5
      }));
    } else {
      this.trail = new THREE.Line(trailGeometry, new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.85
      }));
    }

    this.scene.add(this.trail);
  }

  updatePosition(position) {
    if (this.mesh) {
      this.mesh.position.set(position.x, position.y, position.z);

      if (this.core) {
        const t = performance.now() * 0.02 + this.flickerSeed;
        this.glow.scale.setScalar(1 + Math.sin(t) * 0.12 + Math.sin(t * 2.3) * 0.06);
        if (this.tumble) {
          this.core.rotation.x += 0.12;
          this.core.rotation.z += 0.07;
        } else {
          this.core.rotation.y = t * 0.3;
        }
      }

      // Update trail
      this.updateTrail(position);
    }
  }

  updateTrail(position) {
    if (!this.trail) return;

    // Only record new points when the projectile has actually moved
    const last = this.trailPositions[this.trailPositions.length - 1];
    if (last && Math.abs(last.x - position.x) + Math.abs(last.y - position.y) + Math.abs(last.z - position.z) < 0.05) {
      return;
    }

    // Add current position to trail history
    this.trailPositions.push({ x: position.x, y: position.y, z: position.z });

    // Limit trail length
    while (this.trailPositions.length > this.maxTrailLength) {
      this.trailPositions.shift();
    }

    // Update trail geometry (shared with the ember points for fireballs)
    const positionAttr = this.trail.geometry.attributes.position;
    const positions = positionAttr.array;
    const count = this.trailPositions.length;
    for (let i = 0; i < count; i++) {
      const pos = this.trailPositions[i];
      // Embers drift upwards a little as they cool
      const rise = this.isFire ? (count - 1 - i) * 0.08 : 0;
      positions[i * 3] = pos.x;
      positions[i * 3 + 1] = pos.y + rise;
      positions[i * 3 + 2] = pos.z;
    }
    positionAttr.needsUpdate = true;
    this.trail.geometry.setDrawRange(0, count);

    if (this.embers) {
      const colorAttr = this.embers.geometry.attributes.color;
      const ember = new THREE.Color();
      for (let i = 0; i < count; i++) {
        // Newest ember is hottest; older ones cool and fade to black (invisible under additive blending)
        const age = count > 1 ? 1 - i / (count - 1) : 0;
        ember.copy(EMBER_HOT).lerp(EMBER_COOL, age).multiplyScalar(1 - age * 0.85);
        colorAttr.setXYZ(i, ember.r, ember.g, ember.b);
      }
      colorAttr.needsUpdate = true;
      this.embers.geometry.setDrawRange(0, count);
    }
  }

  remove() {
    // Shared geometries and materials stay alive; only per-projectile resources are disposed
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh = null;
      this.core = null;
      this.glow = null;
    }

    if (this.embers) {
      this.scene.remove(this.embers);
      this.embers.geometry.dispose();
      this.embers.material.dispose();
      this.embers = null;
    }

    if (this.trail) {
      this.scene.remove(this.trail);
      if (this.trail.geometry) {
        this.trail.geometry.dispose();
      }
      if (this.trail.material) {
        this.trail.material.dispose();
      }
      this.trail = null;
    }
  }
}
